import jsPDF from 'jspdf';

interface RatingScale {
  label: string;
  color: string;
  total_evaluations: number;
}

interface ExportPerformanceRatingOptions {
  organisationName: string;
  ratingScales: RatingScale[];
  totalEvaluations: number;
  distributionData: any[];
  organisationLogo: string;
  groupBy: string;
}

const generateHTML = (
  organisationName: string,
  ratingScales: RatingScale[],
  totalEvaluations: number,
  distributionData: any[],
  organisationLogo: string,
  groupBy: string,
): string => {
  const currentDate = new Date().toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  const getPercentage = (value: number) => {
    if (!totalEvaluations || totalEvaluations === 0) return 0;
    return Math.round((value / totalEvaluations) * 100);
  };

  const visibleScales = ratingScales.filter((s) => s.total_evaluations > 0);

  const ratingItemsHTML = visibleScales
    .map(
      (scale) => `
    <div class="rating-item">
      <div class="rating-row">
        <div class="rating-left">
          <div class="rating-score">${scale.total_evaluations}</div>
          <div class="rating-label">${scale.label}</div>
        </div>
        <div class="rating-separator"></div>
        <div class="bar-container">
          <div class="bar" style="width: ${getPercentage(
            scale.total_evaluations
          )}%; background: ${scale.color};"></div>
        </div>
      </div>
    </div>
  `
    )
    .join('');

  // Generate legend items HTML
  const legendItemsHTML = ratingScales
    .map(
      (scale) => `
    <div class="legend-item">
      <div class="legend-dot" style="background: ${scale.color};"></div>
      <div class="legend-label">${scale.label}</div>
    </div>
  `
    )
    .join('');

  // Generate table rows HTML
  const tableRowsHTML = (distributionData || [])
    .map((item) => {
      const distributionHTML = (item.rating_scales || [])
        .map((scale: any) => {
          if (scale.total_evaluations === 0) return '';
          return `<div class="dist-item">
          <div class="dist-dot" style="background: ${scale.color};"></div>
          <span>${scale.total_evaluations}</span>
          </div>`;
        })
        .join('');

      return `
      <tr>
        <td>${item.name || ''}</td>
        <td>${item.category || ''}</td>
        <td>${item.total_evaluations || 0}</td>
        <td>
          <div class="distribution">
            ${distributionHTML}
          </div>
        </td>
      </tr>
    `;
    })
    .join('');

  return `
    <div style="width: 794px; min-height: 1120px; background: #ffffff; 
    font-family: 'Helvetica', Arial, sans-serif; display: flex; flex-direction: column;">
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { -webkit-font-smoothing: antialiased; }
        
        .header { 
          background: #1a3a5c; 
          color: white; 
          padding: 24px; 
          display: flex; 
          justify-content: space-between; 
          align-items: center; 
        }
        .header-left h1 { 
          font-size: 16px; 
          font-weight: 600; 
          margin-bottom: 4px; 
          letter-spacing: -0.3px; 
          color: white; 
        }
        .header-left .date { 
          font-size: 14px; 
          opacity: 0.85; 
          font-weight: 400; 
        }
        .header-right { 
          display: flex; 
          align-items: center; 
          gap: 10px; 
        }
        .logo-text { 
          font-size: 14px; 
          font-weight: 500; 
          letter-spacing: -0.2px; 
          color: white; 
        }
        .logo-icon { 
          width: 36px; 
          height: 36px; 
          background: #0071dc; 
          border-radius: 8px; 
          display: flex; 
          align-items: center; 
          justify-content: center; 
          font-size: 24px; 
          font-weight: 700; 
        }
        .content {
          flex: 1;
        }
        .section { 
          padding: 20px 20px 20px 20px; 
        }
        .section-title { 
          font-size: 12px; 
          font-weight: 600; 
          color: #27272A; 
          margin-bottom: 16px; 
          letter-spacing: -0.2px; 
        }
        .rating-item { 
          margin-bottom: 16px; 
        }
        .rating-row { 
          display: flex; 
          align-items: center; 
          gap: 20px; 
        }
        .rating-left { 
          min-width: 140px; 
          flex-shrink: 0; 
        }
        .rating-score { 
          font-size: 12px; 
          font-weight: 600; 
          font-family: 'Inter', sans-serif;
          color: #27272A; 
          line-height: 1; 
          letter-spacing: -0.5px; 
        }
        .rating-label { 
          font-size: 10px; 
          color: #52525B; 
          font-weight: 500; 
        }
        .rating-separator { 
          width: 1px; 
          height: 20px; 
          background: #E4E4E7; 
        }
        .bar-container { 
          height: 10px; 
          background: #f3f4f6; 
          border-radius: 7px; 
          overflow: hidden; 
          position: relative; 
          flex: 1; 
        }
        .bar { 
          height: 100%; 
          border-radius: 7px; 
        }
        .legend { 
          display: flex; 
          gap: 12px; 
          flex-wrap: wrap; 
          margin-bottom: 16px; 
        }
        .legend-item { 
          display: flex; 
          align-items: center; 
          gap: 8px; 
        }
        .legend-dot { 
    width: 6px;
    height: 10px;
    border-radius: 2px;
    margin-top: 4px;
        }
        .legend-label { 
          font-size: 10px; 
          color: #52525B; 
          font-weight: 500; 
        }
        .table-wrapper { 
          border: 1px solid #e5e7eb; 
          border-radius: 8px; 
          overflow: hidden; 
        }
        .table { 
          width: 100%; 
          border-collapse: collapse; 
        }
        .table thead { 
          background: #f9fafb; 
        }
        .table th { 
          text-align: left; 
          font-size: 11px; 
          font-weight: 600; 
          color: #6b7280; 
          text-transform: uppercase; 
          letter-spacing: 0.8px; 
          padding: 10px 20px; 
        }
        .table td { 
          padding: 10px 20px; 
          border-top: 1px solid #f3f4f6; 
          font-size: 10px; 
          color: #1f2937; 
          vertical-align: middle; 
        }
        .table td:first-child { 
          font-weight: 500; 
        }
        .table td:nth-child(2) { 
          color: #6b7280; 
        }
        .table td:nth-child(3) { 
          font-weight: 500; 
          color: #4b5563; 
        }
        .distribution { 
          display: flex; 
          gap: 12px; 
          align-items: center; 
        }
        .dist-item { 
          display: flex; 
          align-items: center; 
          gap: 6px; 
          font-size: 13px; 
          font-weight: 500; 
          color: #4b5563; 
        }
        .dist-dot { 
    width: 6px;
    height: 10px;
    border-radius: 2px;
    flex-shrink: 0;
        }
        .footer { 
          padding: 24px 48px; 
          border-top: 1px solid #e5e7eb; 
          display: flex; 
          justify-content: space-between; 
          align-items: center; 
          margin-top: auto; 
        }
        .footer-left { 
          display: flex; 
          align-items: center; 
          gap: 6px; 
          font-size: 12px; 
          color: #6b7280; 
        }
        .footer-left strong { 
          color: #1f2937; 
          font-weight: 600; 
        }
        .footer-right { 
          font-size: 12px; 
          color: #6b7280; 
        }
      </style>

      <div class="header">
        <div class="header-left">
          <h1>Performance rating report</h1>
          <div class="date">${currentDate}</div>
        </div>
        <div class="header-right">
          <span class="logo-text">${organisationName}</span>
          <div class="logo-icon"><img src="${organisationLogo}" alt="${organisationName}" crossorigin="anonymous" /></div>
        </div>
      </div>

      <div class="content">
        <div class="section">
          <div class="section-title">Organization overall</div>
          ${ratingItemsHTML}
        </div>

        <div class="section">
          <div class="section-title">Percentage distribution</div>
          
          <div class="legend">
            ${legendItemsHTML}
          </div>
          
          <div class="table-wrapper">
            <table class="table">
              <thead>
                <tr>
                  <th style="width: 22%;">COMPETENCY</th>
                  <th style="width: 24%;">${groupBy === 'function' ? 'FUNCTION' : 'CATEGORY'}</th>
                  <th style="width: 14%;">REVIEWS</th>
                  <th style="width: 40%;">DISTRIBUTION</th>
                </tr>
              </thead>
              <tbody>
                ${tableRowsHTML}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="footer">
        <div class="footer-left">
          <span>Powered by</span>
          <strong>oneteam</strong>
        </div>
        <div class="footer-right">Page 1 of 1</div>
      </div>
    </div>
  `;
};

export const exportPerformanceRatingReportPDF = async ({
  organisationName,
  ratingScales,
  totalEvaluations,
  distributionData,
  organisationLogo,
  groupBy,
}: ExportPerformanceRatingOptions) => {
  const htmlContent = generateHTML(
    organisationName,
    ratingScales,
    totalEvaluations,
    distributionData,
    organisationLogo,
    groupBy,
  );

  // Create temporary container
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlContent;

  // eslint-disable-next-line new-cap
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  return new Promise<void>((resolve) => {
    doc.html(tempDiv, {
      callback: (document) => {
        const fileName = `performance-rating-report-${
          new Date().toISOString().split('T')[0]
        }.pdf`;
        document.save(fileName);
        resolve();
      },
      x: 0,
      y: 0,
      width: 210,
      windowWidth: 794,
    });
  });
};
